'use client';

import { useEffect, useState, useCallback } from 'react';

/**
 * Static UI element heights (in pixels)
 * These are approximate values for calculating available image height
 */
const DEFAULT_STATIC_HEIGHTS = {
  /** Mobile: header + tabs + padding */
  mobile: 136,
  /** Desktop: similar but more horizontal space available */
  desktop: 100,
};

/**
 * Reserve space below the image for controls/feedback banners.
 * Hybrid: 6% of viewport capped at 60px — feedback banners use fixed font
 * sizes, so a pure percentage wastes space on tall windows. The cap keeps the
 * image area as large as possible on large screens.
 */
const RESERVE_PERCENTAGE = 0.06;
const RESERVE_MAX_PX = 60;

interface UseMaxImageHeightOptions {
  /** Static elements height in pixels (default: auto-detected by viewport width) */
  staticElementsHeight?: number;
  /** Whether the hook is enabled (default: true) */
  enabled?: boolean;
}

interface UseMaxImageHeightResult {
  /** Maximum allowed image height in pixels, or null if not yet calculated */
  maxHeight: number | null;
  /** Current viewport height in pixels */
  viewportHeight: number;
  /** Calculate constrained dimensions for a given aspect ratio */
  getConstrainedDimensions: (
    aspectRatio: number,
    containerWidth: number
  ) => {
    width: number;
    height: number;
    isHeightConstrained: boolean;
  };
}

/**
 * Hook to calculate maximum allowed image height based on viewport
 *
 * Ensures images on concept pages don't exceed available viewport height,
 * accounting for static UI elements and reserving ~10% for controls below.
 *
 * For vertical/portrait images, calculates reduced width to maintain aspect ratio.
 *
 * @example
 * ```tsx
 * function ImageContainer() {
 *   const { maxHeight, getConstrainedDimensions } = useMaxImageHeight();
 *   const { width, height, isHeightConstrained } = getConstrainedDimensions(aspectRatio, containerWidth);
 *
 *   return (
 *     <div style={{
 *       maxHeight: maxHeight ?? undefined,
 *       width: isHeightConstrained ? width : '100%',
 *       margin: '0 auto'
 *     }}>
 *       <Image ... />
 *     </div>
 *   );
 * }
 * ```
 */
export function useMaxImageHeight(
  options: UseMaxImageHeightOptions = {}
): UseMaxImageHeightResult {
  const { staticElementsHeight, enabled = true } = options;

  const [viewportHeight, setViewportHeight] = useState(
    typeof window !== 'undefined' ? window.innerHeight : 0
  );
  const [maxHeight, setMaxHeight] = useState<number | null>(null);

  useEffect(() => {
    if (!enabled) {
      setMaxHeight(null);
      return;
    }

    const calculate = () => {
      const vh = window.innerHeight;
      setViewportHeight(vh);

      // Use provided static height or auto-detect based on viewport width
      const staticHeight =
        staticElementsHeight ??
        (window.innerWidth < 768
          ? DEFAULT_STATIC_HEIGHTS.mobile
          : DEFAULT_STATIC_HEIGHTS.desktop);

      const reserve = Math.min(vh * RESERVE_PERCENTAGE, RESERVE_MAX_PX);

      // Calculate max height
      const calculatedMaxHeight = vh - staticHeight - reserve;

      // Ensure minimum reasonable height (at least 200px)
      setMaxHeight(Math.max(200, calculatedMaxHeight));
    };

    calculate();

    // Listen to resize events
    window.addEventListener('resize', calculate);

    // Also listen to orientation change for mobile
    window.addEventListener('orientationchange', calculate);

    return () => {
      window.removeEventListener('resize', calculate);
      window.removeEventListener('orientationchange', calculate);
    };
  }, [enabled, staticElementsHeight]);

  /**
   * Calculate constrained dimensions for a given aspect ratio
   * If the natural height would exceed maxHeight, reduce both dimensions
   * while maintaining aspect ratio
   */
  const getConstrainedDimensions = useCallback(
    (
      aspectRatio: number,
      containerWidth: number
    ): { width: number; height: number; isHeightConstrained: boolean } => {
      if (!maxHeight || !enabled) {
        // No constraint - use full width
        const naturalHeight = containerWidth / aspectRatio;
        return {
          width: containerWidth,
          height: naturalHeight,
          isHeightConstrained: false,
        };
      }

      // Calculate what height would be at full container width
      const naturalHeight = containerWidth / aspectRatio;

      if (naturalHeight <= maxHeight) {
        // Image fits within max height at full width
        return {
          width: containerWidth,
          height: naturalHeight,
          isHeightConstrained: false,
        };
      }

      // Height exceeds max - constrain to max height and calculate width
      const constrainedWidth = maxHeight * aspectRatio;

      // Ensure minimum width (50% of container)
      const minWidth = containerWidth * 0.5;
      const finalWidth = Math.max(minWidth, constrainedWidth);
      const finalHeight = finalWidth / aspectRatio;

      return {
        width: finalWidth,
        height: Math.min(finalHeight, maxHeight),
        isHeightConstrained: true,
      };
    },
    [maxHeight, enabled]
  );

  return {
    maxHeight,
    viewportHeight,
    getConstrainedDimensions,
  };
}
